import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import {
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
} from "@tabler/icons-react";
import { VoiceoverInfo } from "./VoiceoverInfo";
import { Button } from "@/lib/components/Button/Button";
import { VoiceoverPreview } from "./VoiceoverBottomBar";
import { formatAudioDuration, parseHtmlToPlainText } from "@/lib/utils";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/lib/components/Dialog";
import { useState } from "react";
import { NavigationButton } from "../NavigationButton";

const hasScriptChanged = (currentScript: string, textareaScript: string) => {
  return currentScript !== textareaScript;
};

function EarlierVoiceovers() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedVoiceoverId, setSelectedVoiceoverId] = useState<string | null>(
    null,
  );

  const updateVoiceoverTabState = useEditorStore(
    (state) => state.service.updateVoiceoverTabState,
  );
  const voiceOvers = useEditorStore((state) => state.voiceOvers);
  const setCurrentVoiceOver = useEditorStore(
    (state) => state.service.setCurrentVoiceOver,
  );
  const setPreviewVoiceOver = useEditorStore(
    (state) => state.service.setPreviewVoiceOver,
  );
  const togglePreviewVoiceOverPlayback = useEditorStore(
    (state) => state.service.togglePreviewVoiceOverPlayback,
  );
  const currentVoiceOver = useEditorStore((state) =>
    state.service.getCurrentVoiceOver(),
  );
  const previewVoiceOver = useEditorStore((state) => state.previewVoiceOver);
  const isPreviewPlaying = useEditorStore(
    (state) => state.isPreviewVoiceOverPlaying,
  );

  const { script } = useEditorStore((state) => state.voiceOverForm);
  const scriptHasChanged =
    currentVoiceOver && hasScriptChanged(currentVoiceOver.script, script);

  const handleUseVoiceover = (voiceoverId: string) => {
    updateVoiceoverTabState((state) => {
      state.view = "CREATE_VOICEOVER";
    });
    setCurrentVoiceOver(voiceoverId);
  };

  return (
    <div className="relative flex h-full animate-fade-in flex-col">
      <NavigationButton
        text="Earlier voiceovers"
        onClick={() => {
          updateVoiceoverTabState((state) => {
            state.view = "CREATE_VOICEOVER";
          });
        }}
      />
      <div className="relative grow">
        <div className="absolute inset-0 flex flex-col gap-1 overflow-y-auto px-6 pt-3 pb-6">
          {voiceOvers.map((voiceOver) => {
            const isCurrentVoiceOver =
              currentVoiceOver?.externalId === voiceOver.externalId;
            const isCurrentPreviewVoiceover =
              previewVoiceOver?.externalId === voiceOver.externalId;

            return (
              <VoiceoverInfo
                key={voiceOver.externalId}
                title={parseHtmlToPlainText(voiceOver.script)}
                actor={voiceOver.voiceActorName}
                duration={formatAudioDuration(voiceOver.durationMs)}
                createdAt={voiceOver.createdAt}
                isCurrentVoiceover={isCurrentVoiceOver}
                variant="list"
                actionsChildren={
                  <>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        if (isCurrentPreviewVoiceover) {
                          togglePreviewVoiceOverPlayback();
                        } else {
                          setPreviewVoiceOver(voiceOver);
                        }
                      }}
                    >
                      {isCurrentPreviewVoiceover && isPreviewPlaying ? (
                        <IconPlayerPauseFilled />
                      ) : (
                        <IconPlayerPlayFilled />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (scriptHasChanged) {
                          setSelectedVoiceoverId(voiceOver.externalId);
                          setDialogOpen(true);
                        } else {
                          handleUseVoiceover(voiceOver.externalId);
                        }
                      }}
                    >
                      Use
                    </Button>
                  </>
                }
              />
            );
          })}
        </div>
        <div className="pointer-events-none absolute right-0 -bottom-1 left-0 h-16 bg-linear-to-t from-white" />
      </div>
      <VoiceoverPreview />
      <ConfirmOverwriteDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onConfirm={() => {
          if (selectedVoiceoverId) {
            handleUseVoiceover(selectedVoiceoverId);
          }
        }}
      />
    </div>
  );
}

function ConfirmOverwriteDialog({
  open,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="w-full">
          <DialogTitle>Overwrite script?</DialogTitle>
          <DialogDescription>
            You have unsaved changes to the script. Do you want to overwrite
            them with this voiceover's script?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-0">
          <Button
            onClick={() => {
              onConfirm();
              onOpenChange(false);
            }}
          >
            Overwrite
          </Button>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EarlierVoiceovers;
